import type {Values} from '@wix/devzai-utils-common';
import {DoppeProductsGridLayoutSize} from '../doppe-products-grid-layout-size';
import {DoppeActionLayoutMediaRatio} from '../doppe-action-layout';
import {DoppeRowTemplateButtonDesign} from '../doppe-row-template-button-design/doppe-row-template-button-design';

/**
 * Predefined template IDs for product list pages.
 * Each template defines a different visual layout and behavior.
 */
export const DoppeProductListPageTemplateId = {
    /**
     * Frameless template - clean grid layout without card borders.
     * Image and text displayed without container borders.
     * Uses DoppeProductsGridLayoutSize to control item size.
     */
    FramelessTemplate: 'FramelessTemplate',

    /**
     * Card template with bordered card containers.
     * Each product is displayed in a card with subtle borders.
     * Uses DoppeProductsGridLayoutSize to control item size.
     */
    CardTemplate: 'CardTemplate',

    /**
     * On-image template with text overlay on the product image.
     * Product info is displayed on top of the image.
     * Uses DoppeProductsGridLayoutSize to control item size.
     * Fixed image ratio (not editable).
     */
    OnImageTemplate: 'OnImageTemplate',

    /**
     * Row template with horizontal list layout.
     * Each product is displayed as a full-width row.
     * Uses flex column layout (not grid).
     * Does not support layout size or custom image ratio.
     */
    RowTemplate: 'RowTemplate',
} as const;

export type DoppeProductListPageTemplateId = Values<typeof DoppeProductListPageTemplateId>;

/**
 * Template kind for product list pages.
 */
export const DoppeProductListPageTemplateKind = {
    /**
     * A predefined template with a specific templateId.
     */
    Predefined: 'predefined',
} as const;

export type DoppeProductListPageTemplateKind = Values<typeof DoppeProductListPageTemplateKind>;


/**
 * Template selector for product list pages.
 * Specifies which template to use for rendering the page.
 */
export type DoppeProductListPageTemplate = DoppeProductListPageTemplate.Predefined;

export namespace DoppeProductListPageTemplate {
    /**
     * A predefined template with a specific templateId.
     */
    export interface Predefined {
        kind: typeof DoppeProductListPageTemplateKind.Predefined;
        templateId: DoppeProductListPageTemplateId;
    }
}

/**
 * Template-specific props for each product list page template.
 * Each template has its own set of configurable properties.
 */
export type DoppeProductListPageTemplateProps = 
    | DoppeProductListPageTemplateProps.FramelessTemplate
    | DoppeProductListPageTemplateProps.CardTemplate
    | DoppeProductListPageTemplateProps.OnImageTemplate
    | DoppeProductListPageTemplateProps.RowTemplate
    // Legacy types for backward compatibility
    | DoppeProductListPageTemplateProps.GridTemplate
    | DoppeProductListPageTemplateProps.ListTemplate;

export namespace DoppeProductListPageTemplateProps {
    /**
     * Props for the FramelessTemplate.
     * Clean grid layout without card borders.
     * Uses responsive grid with min/max widths based on layout size.
     */
    export interface FramelessTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeProductListPageTemplateId.FramelessTemplate;

        /**
         * Layout size for the products grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;

        /**
         * Image aspect ratio for product images.
         */
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * Props for the CardTemplate.
     * Grid layout with bordered card containers.
     * Uses responsive grid with min/max widths based on layout size.
     */
    export interface CardTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeProductListPageTemplateId.CardTemplate;

        /**
         * Layout size for the products grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;

        /**
         * Image aspect ratio for product images.
         */
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * Props for the OnImageTemplate.
     * Grid layout with text overlay on images.
     * Uses responsive grid with min/max widths based on layout size.
     * For Small layout size: only vertical ratios (1:1, 4:5, 3:4, 2:3, 9:16).
     * For Medium layout size: full range of ratios available.
     */
    export interface OnImageTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeProductListPageTemplateId.OnImageTemplate;

        /**
         * Layout size for the products grid.
         * - Small: Smaller cards with different min widths for mobile (150px) and desktop (280px)
         * - Medium: Medium cards (min 380px, fluid max) - default
         */
        layoutSize: DoppeProductsGridLayoutSize;
        
        /**
         * Image aspect ratio for product images.
         * For Small layout size: only vertical ratios are available (1:1, 4:5, 3:4, 2:3, 9:16).
         * For Medium layout size: full range of ratios available.
         */
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * Props for the RowTemplate.
     * Flex column layout with horizontal rows.
     * Does not support layout size or custom image ratio.
     */
    export interface RowTemplate {
        /**
         * Template identifier for discriminated union.
         */
        templateType: typeof DoppeProductListPageTemplateId.RowTemplate;
        
        /**
         * Whether to show the media/image in the row template.
         * When false, the image container is hidden and only text content is shown.
         */
        showMedia: boolean;
        
        // Note: No layoutSize - uses flex column layout
        // Note: No imageRatio - uses fixed ratio
    }

    // Legacy interfaces for backward compatibility
    /**
     * @deprecated Use FramelessTemplate instead
     */
    export interface GridTemplate {
        layoutSize: DoppeProductsGridLayoutSize;
        imageRatio: DoppeActionLayoutMediaRatio;
    }

    /**
     * @deprecated Use RowTemplate instead
     */
    export interface ListTemplate {
        imageRatio: DoppeActionLayoutMediaRatio;
    }
}

/**
 * Common props shared by all product list page templates.
 * These props are template-agnostic and control shared behavior.
 */
export interface DoppeProductListPageCommonProps {
    /**
     * Whether to show the product price.
     * @default true
     */
    showPrice: boolean;

    /**
     * Whether to show "Excluding VAT" text below the price.
     * @default false
     */
    showPriceIncludedTax: boolean;

    /**
     * Whether to show the product description.
     * Only visible when layout size is Medium (Large) - hidden in Small size.
     * @default true
     */
    showDescription: boolean;
    
    /**
     * Whether to show the Buy button on product cards.
     * Only applicable to RowTemplate - other templates don't show the button.
     * Clicking the button navigates to the product page.
     * @default false
     */
    showBuyNowButton: boolean;
    
    /**
     * Custom text for the Buy button.
     * Only applicable to RowTemplate.
     * @default "Buy now"
     */
    buyButtonText: string;

    /**
     * The visual design of the action button in row template.
     * - Icon: Textual link with arrow icon
     * - IconButton: Circular icon button
     * - Button: Full text button
     * - None: No button displayed
     * Only applicable to RowTemplate.
     * @default "button"
     */
    buttonDesign: DoppeRowTemplateButtonDesign;

    /**
     * Whether to show the horizontal categories menu above the list.
     * @default true
     */
    showCategoriesMenu: boolean;

    /**
     * Whether to show the product ribbon (badge) on the product image.
     * @default true
     */
    showRibbon: boolean;
}

/**
 * Creates default template settings for a product list page.
 * @param templateId - Optional template ID. Uses FramelessTemplate if not specified.
 */
export function doppeProductListPageTemplateCreateDefault(
    templateId: DoppeProductListPageTemplateId = DoppeProductListPageTemplateId.FramelessTemplate
): DoppeProductListPageTemplate {
    return {
        kind: DoppeProductListPageTemplateKind.Predefined,
        templateId: templateId,
    };
}

/**
 * Creates default template props for a given template.
 */
export function doppeProductListPageTemplatePropsCreateDefault(
    templateId: DoppeProductListPageTemplateId
): DoppeProductListPageTemplateProps {
    switch (templateId) {
        case DoppeProductListPageTemplateId.FramelessTemplate:
            return {
                templateType: DoppeProductListPageTemplateId.FramelessTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Large,
                imageRatio: DoppeActionLayoutMediaRatio.Square,
            };
        case DoppeProductListPageTemplateId.CardTemplate:
            return {
                templateType: DoppeProductListPageTemplateId.CardTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Large,
                imageRatio: DoppeActionLayoutMediaRatio.Square,
            };
        case DoppeProductListPageTemplateId.OnImageTemplate:
            return {
                templateType: DoppeProductListPageTemplateId.OnImageTemplate,
                layoutSize: DoppeProductsGridLayoutSize.Large,
                imageRatio: DoppeActionLayoutMediaRatio.Square,
            };
        case DoppeProductListPageTemplateId.RowTemplate:
            return {
                templateType: DoppeProductListPageTemplateId.RowTemplate,
                showMedia: true,
            };
    }
}

/**
 * Creates default common props for a product list page.
 */
export function doppeProductListPageCommonPropsCreateDefault(): DoppeProductListPageCommonProps {
    return {
        showPrice: true,
        showPriceIncludedTax: false,
        showDescription: true,
        showBuyNowButton: false,
        buyButtonText: 'Buy now',
        buttonDesign: DoppeRowTemplateButtonDesign.Button,
        showCategoriesMenu: false,
        showRibbon: true,
    };
}

/**
 * Type guard to check if template props are for FramelessTemplate.
 */
export function doppeProductListPageTemplatePropsIsFramelessTemplate(
    props: DoppeProductListPageTemplateProps
): props is DoppeProductListPageTemplateProps.FramelessTemplate {
    return 'templateType' in props && props.templateType === DoppeProductListPageTemplateId.FramelessTemplate;
}

/**
 * Type guard to check if template props are for CardTemplate.
 */
export function doppeProductListPageTemplatePropsIsCardTemplate(
    props: DoppeProductListPageTemplateProps
): props is DoppeProductListPageTemplateProps.CardTemplate {
    return 'templateType' in props && props.templateType === DoppeProductListPageTemplateId.CardTemplate;
}

/**
 * Type guard to check if template props are for OnImageTemplate.
 */
export function doppeProductListPageTemplatePropsIsOnImageTemplate(
    props: DoppeProductListPageTemplateProps
): props is DoppeProductListPageTemplateProps.OnImageTemplate {
    return 'templateType' in props && props.templateType === DoppeProductListPageTemplateId.OnImageTemplate;
}

/**
 * Type guard to check if template props are for RowTemplate.
 */
export function doppeProductListPageTemplatePropsIsRowTemplate(
    props: DoppeProductListPageTemplateProps
): props is DoppeProductListPageTemplateProps.RowTemplate {
    return 'templateType' in props && props.templateType === DoppeProductListPageTemplateId.RowTemplate;
}

/**
 * Type guard to check if template props support layout size configuration.
 * FramelessTemplate, CardTemplate, and OnImageTemplate support layout size.
 * RowTemplate does not support layout size.
 */
export function doppeProductListPageTemplatePropsSupportsLayoutSize(
    props: DoppeProductListPageTemplateProps
): props is DoppeProductListPageTemplateProps.FramelessTemplate | DoppeProductListPageTemplateProps.CardTemplate | DoppeProductListPageTemplateProps.OnImageTemplate {
    return 'layoutSize' in props;
}

/**
 * Type guard to check if template props support image ratio configuration.
 * FramelessTemplate, CardTemplate, and OnImageTemplate support image ratio.
 * RowTemplate has fixed ratio.
 */
export function doppeProductListPageTemplatePropsSupportsImageRatio(
    props: DoppeProductListPageTemplateProps
): props is DoppeProductListPageTemplateProps.FramelessTemplate | DoppeProductListPageTemplateProps.CardTemplate | DoppeProductListPageTemplateProps.OnImageTemplate {
    return 'imageRatio' in props && 'layoutSize' in props;
}

/**
 * Type guard to check if template uses grid layout.
 * FramelessTemplate, CardTemplate, and OnImageTemplate use grid layout.
 * RowTemplate uses flex column layout.
 */
export function doppeProductListPageTemplatePropsUsesGridLayout(
    props: DoppeProductListPageTemplateProps
): boolean {
    if (!('templateType' in props)) {
        // Legacy props - check for legacy GridTemplate or CardTemplate
        return 'layoutSize' in props;
    }
    return props.templateType !== DoppeProductListPageTemplateId.RowTemplate;
}

/**
 * @deprecated Use doppeProductListPageTemplatePropsIsFramelessTemplate instead
 * Type guard to check if template props are for GridTemplate (legacy).
 */
export function doppeProductListPageTemplatePropsIsGridTemplate(
    props: DoppeProductListPageTemplateProps
): props is DoppeProductListPageTemplateProps.GridTemplate {
    return 'layoutSize' in props && !('templateType' in props);
}

